import React from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import {
  TrendUp, Users, Globe, Translate, ShieldCheck, Wallet,
  ChartLineUp, Briefcase, MapPinLine, Handshake, Sparkle,
  Lightning, Lightbulb, Target, Buildings, GraduationCap,
  CurrencyInr, ArrowRight, Quotes, CheckCircle, WarningOctagon,
  RocketLaunch, HandHeart,
} from "@phosphor-icons/react";

function Stat({ value, label, sub, tid }) {
  return (
    <div className="as-card p-6" data-testid={tid}>
      <div className="text-4xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{value}</div>
      <div className="text-sm font-bold uppercase tracking-wider text-[var(--as-accent)] mt-2">{label}</div>
      {sub && <div className="text-xs text-[var(--as-text-secondary)] mt-1.5 leading-relaxed">{sub}</div>}
    </div>
  );
}

function Section({ kicker, title, children, id }) {
  return (
    <section id={id} className="py-16 md:py-20 border-b border-[var(--as-border)]">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {kicker && (
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)] mb-3">{kicker}</div>
        )}
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--as-primary)] mb-8" style={{fontFamily:"Inter"}}>
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

function Pain({ icon: Icon, title, desc }) {
  return (
    <div className="as-card p-6 border-l-4 !border-l-[var(--as-accent)]">
      <Icon size={28} weight="duotone" color="#00B7A6" />
      <div className="mt-3 font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{title}</div>
      <p className="text-sm text-[var(--as-text-secondary)] mt-1.5 leading-relaxed">{desc}</p>
    </div>
  );
}

function Pillar({ icon: Icon, title, desc }) {
  return (
    <div className="as-card p-6">
      <div className="w-12 h-12 rounded-xl bg-[var(--as-bg-alt)] grid place-items-center border border-[var(--as-border)] mb-4">
        <Icon size={26} weight="duotone" color="#080F14" />
      </div>
      <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{title}</div>
      <p className="text-sm text-[var(--as-text-secondary)] mt-1.5 leading-relaxed">{desc}</p>
    </div>
  );
}

function RevenueRow({ stream, who, model, tag }) {
  return (
    <tr className="border-t border-[var(--as-border)]">
      <td className="p-4 font-bold text-[var(--as-primary)]">{stream}</td>
      <td className="p-4 text-sm text-[var(--as-text-secondary)]">{who}</td>
      <td className="p-4 text-sm">{model}</td>
      <td className="p-4"><span className="as-badge-pill">{tag}</span></td>
    </tr>
  );
}

function Story({ tag, name, role, location, beforeLine, afterLine, quote, kpis, accent }) {
  return (
    <div className="as-card p-7 relative overflow-hidden">
      <span className="absolute top-5 right-5 as-badge-pill text-[10px]">{tag}</span>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-[var(--as-bg-alt)] grid place-items-center text-xl font-extrabold text-[var(--as-primary)] border border-[var(--as-border)]" style={{fontFamily:"Inter"}}>
          {name[0]}
        </div>
        <div>
          <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{name}</div>
          <div className="text-xs text-[var(--as-text-secondary)]">{role} · {location}</div>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--as-text-secondary)] mt-0.5 shrink-0 w-12">Before</span>
          <span className="text-[var(--as-text-secondary)]">{beforeLine}</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--as-accent)] mt-0.5 shrink-0 w-12">After</span>
          <span className="text-[var(--as-primary)] font-semibold">{afterLine}</span>
        </div>
      </div>
      {quote && (
        <p className="mt-4 italic text-[var(--as-primary)] border-l-2 border-[var(--as-accent)] pl-3 text-sm leading-relaxed">
          “{quote}”
        </p>
      )}
      <div className="mt-5 grid grid-cols-3 gap-2 pt-4 border-t border-[var(--as-border)]">
        {kpis.map((k, i) => (
          <div key={i}>
            <div className={`text-xl font-extrabold ${accent ? "text-[var(--as-accent)]" : "text-[var(--as-primary)]"}`} style={{fontFamily:"Inter"}}>{k.v}</div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--as-text-secondary)] mt-0.5">{k.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Investors() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)] mb-5">
            <Sparkle size={14} weight="fill" /> Investor brief · Series Seed
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[var(--as-primary)]" style={{fontFamily:"Inter"}} data-testid="investors-hero">
            India has the world's largest skilled workforce.<br />
            <span className="text-[var(--as-accent)]">It is also the world's most invisible.</span>
          </h1>
          <p className="mt-6 text-lg text-[var(--as-text-secondary)] max-w-3xl leading-relaxed">
            Associate Skill makes 470M+ blue-collar workers and 63M MSMEs <strong>discoverable</strong>,
            <strong> trustworthy</strong>, and <strong>multilingual-by-default</strong> — through visual proof of skill,
            not paperwork. Skills first. Degrees never required.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat value="₹35T" label="Informal economy" sub="~50% of India's GDP runs through informal labor" tid="hero-stat-1" />
            <Stat value="92%" label="Informal workforce" sub="Of India's 470M workers operate without formal records" tid="hero-stat-2" />
            <Stat value="63M+" label="MSMEs" sub="30% of GDP, 110M+ jobs, almost no online presence" tid="hero-stat-3" />
            <Stat value="$1.2T" label="Skilling opportunity" sub="Global re-skilling market by 2030 (WEF)" tid="hero-stat-4" />
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/" className="btn-primary" data-testid="investors-back-home">
              See the product <ArrowRight size={18} weight="bold" />
            </Link>
            <a href="#problem" className="btn-secondary">Why now?</a>
            <a href="#market" className="btn-secondary">Market size</a>
            <a href="#model" className="btn-secondary">Business model</a>
          </div>
        </div>
      </section>

      {/* 30-SECOND THESIS */}
      <section className="bg-[var(--as-primary)] text-[var(--as-bg)] py-14">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#D9C9A6] mb-4">The 30-second thesis</div>
          <p className="text-xl sm:text-2xl md:text-3xl leading-[1.4] font-bold" style={{fontFamily:"Inter"}}>
            India trains <span className="text-[#F5B27D]">10 million skilled workers a year</span> and loses
            <span className="text-[#F5B27D]"> 70% of them to invisibility</span> within 12 months.
            Every uncatalogued worker is a <span className="text-[#F5B27D]">₹2-4 lakh annual GDP leak</span>.
            We turn each one into a searchable, verifiable, multilingual asset — and charge the buyers
            who can't find them today.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
            <div>
              <div className="text-3xl font-extrabold text-[#F5B27D]" style={{fontFamily:"Inter"}}>₹4.2L Cr</div>
              <div className="text-xs uppercase tracking-wider font-bold opacity-80 mt-1">Annual middleman leakage in Indian skilled labor</div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-[#F5B27D]" style={{fontFamily:"Inter"}}>14 days</div>
              <div className="text-xs uppercase tracking-wider font-bold opacity-80 mt-1">Avg. time to hire a verified electrician today</div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-[#F5B27D]" style={{fontFamily:"Inter"}}>83%</div>
              <div className="text-xs uppercase tracking-wider font-bold opacity-80 mt-1">Of MSMEs export &lt;5% of capacity due to discovery gap</div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-[#F5B27D]" style={{fontFamily:"Inter"}}>0</div>
              <div className="text-xs uppercase tracking-wider font-bold opacity-80 mt-1">Multilingual-video skill platforms exist globally today</div>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <Section id="problem" kicker="The problem" title="Talent is everywhere. Trust is nowhere.">
        <p className="text-lg text-[var(--as-text-secondary)] max-w-4xl leading-relaxed mb-10">
          A master carpenter in Pochampally can build the same wardrobe as one in Stockholm.
          A welder in Pune can match a fabricator in Frankfurt. But the carpenter has no LinkedIn,
          no English résumé, and no easy way to prove a decade of craftsmanship.
          Hiring decisions still rely on word-of-mouth, contractor middlemen, and unverifiable claims.
        </p>

        <div className="grid md:grid-cols-2 gap-5">
          <Pain icon={WarningOctagon} title="Résumés exclude 92% of India's workforce"
            desc="Traditional hiring tools assume English literacy and formal qualifications. India's blue-collar majority gets locked out at the door." />
          <Pain icon={Translate} title="Language is a wall, not a feature"
            desc="A Telugu-speaking electrician can't market to Hindi or English buyers. Existing platforms force one language and lose 80% of talent depth." />
          <Pain icon={Buildings} title="MSMEs are invisible to global buyers"
            desc="63M MSMEs drive 30% of GDP, but most are unfindable beyond a 5km word-of-mouth radius. Export potential is locked in." />
          <Pain icon={ShieldCheck} title="Trust is offline and slow"
            desc="Verifying a worker takes weeks of references and site visits. Households default to over-paying middlemen for safety." />
          <Pain icon={CurrencyInr} title="Earnings stay 30-50% below potential"
            desc="Without verifiable proof of skill, workers can't price their craft. Middlemen capture the margin, not the talent." />
          <Pain icon={GraduationCap} title="Skilling investment doesn't translate to jobs"
            desc="Govt + private sector spend ₹15,000 Cr/year on skilling. There is no last-mile bridge to actual employment." />
        </div>
      </Section>

      {/* THE SOLUTION */}
      <Section kicker="The solution" title="A skills-first marketplace, built for video proof and any language.">
        <p className="text-lg text-[var(--as-text-secondary)] max-w-4xl leading-relaxed mb-10">
          We replace the résumé with a 30-second video. We replace English-only with auto-translated
          transcripts in 22+ languages. We replace blind hiring with verified, skill-tagged portfolios.
          Workers and MSMEs build trust once and rent it across every employer, household, and global buyer.
        </p>

        <div className="grid md:grid-cols-3 gap-5">
          <Pillar icon={Lightning} title="Visual portfolio in 5 minutes"
            desc="A welder uploads three clips of their work. Skill tags + auto-transcript. They're discoverable in any language, immediately." />
          <Pillar icon={Translate} title="Multilingual by default"
            desc="Every transcript exists in English, Hindi, Telugu (and more). A buyer in Tokyo can read a Pochampally weaver's pitch in seconds." />
          <Pillar icon={ShieldCheck} title="Trust is composable"
            desc="ID Verified. Certified by Training Partner. Skill India linked. Each badge is reusable — earn once, signal everywhere." />
          <Pillar icon={Target} title="Skill-overlap matching"
            desc="Employers post a job → we rank candidates by skill overlap + location + verification + portfolio depth. No keyword roulette." />
          <Pillar icon={ChartLineUp} title="Built-in trust & safety pipeline"
            desc="Every upload is screened for nudity, vulgarity, and unsafe content before it ever hits a feed — keeping the marketplace family-safe by default." />
          <Pillar icon={RocketLaunch} title="Government-rail ready"
            desc="Architecture plugs into Skill India Digital Hub, NSQF, and DigiLocker — turning public skilling spend into placements." />
        </div>
      </Section>

      {/* 5 STORIES */}
      <Section kicker="5 stories that explain the company" title="What changes when proof of skill becomes portable.">
        <p className="text-lg text-[var(--as-text-secondary)] max-w-4xl leading-relaxed mb-10">
          These are not hypotheticals. Each persona below is live in the demo you're using right now —
          with real profiles, real opportunities, real applications, real reviews. Multiply each by 10 million,
          and you have the company.
        </p>

        <div className="grid md:grid-cols-2 gap-5">
          <Story
            tag="Skilled worker · ₹"
            name="Ravi Kumar"
            role="Master Carpenter, 12 yrs"
            location="Hyderabad · Telugu speaker"
            beforeLine="Earned ₹400/day through a thekedar (middleman). No way to prove 12 years of craft. Same wardrobe install priced 4x by the contractor in front of him."
            afterLine="3 video clips + 7 skill tags + 1 ITI badge → discovered by Nimbus Homes (200-flat builder), shortlisted in 6 hours, hired at ₹1,500/day."
            quote="Outstanding work on our wardrobe. Finished early and clean."
            kpis={[
              { v: "+275%", l: "Daily rate" },
              { v: "6 hrs", l: "Time to hire" },
              { v: "5.0★", l: "First review" },
            ]}
            accent
          />

          <Story
            tag="MSME · Export"
            name="Pochampally Co-op"
            role="Handloom Co-op, 20 weavers"
            location="Pochampally, Telangana"
            beforeLine="Sold through 3 layers of middlemen. Buyer in Bengaluru never knew the co-op existed. ₹350/saree margin captured by intermediaries."
            afterLine="One MSME profile + capacity sheet + 1 ikat sample image → quoted on RFQ for 1,000 sarees from Bharat Retail. Direct relationship, ₹950/saree."
            quote="Quality and consistency on a 200-saree pilot order. Recommended."
            kpis={[
              { v: "₹6L+", l: "Pilot order" },
              { v: "0", l: "Middlemen" },
              { v: "75 days", l: "Lead time" },
            ]}
            accent
          />

          <Story
            tag="Beautician · Bharat"
            name="Priya Sharma"
            role="Bridal Makeup Artist"
            location="Mumbai · Hindi speaker"
            beforeLine="Worked 18 days/month at a salon for ₹15,000 fixed. Bridal gigs flowed only through her ex-employer at a 60% cut."
            afterLine="Portfolio of 6 bridal looks + mehndi videos. A household (Joshi family) found her directly for a 2-day wedding — ₹22,000."
            quote="Lovely work for the bride and family — she's already booked 3 of her cousin's friends."
            kpis={[
              { v: "₹22K", l: "Per gig" },
              { v: "0%", l: "Salon cut" },
              { v: "+9", l: "Referral funnel" },
            ]}
          />

          <Story
            tag="Employer · Hiring"
            name="Nimbus Homes"
            role="Mid-rise residential builder"
            location="Hyderabad · 40-tower portfolio"
            beforeLine="Hiring 4 site electricians took 4-6 weeks via 3 contractors. ₹18K/head finder fee + safety incidents from unverified hires."
            afterLine="Posted 1 opportunity → match-scored 12 candidates by skill+location+verified KYC → shortlisted Suresh Reddy (8 yrs, ITI Licensed) in under 24 hours."
            quote="We're cutting our hiring time from weeks to hours and our finder fees to zero."
            kpis={[
              { v: "−92%", l: "Hiring time" },
              { v: "₹0", l: "Finder fee" },
              { v: "100%", l: "Verified hires" },
            ]}
          />

          <Story
            tag="Global buyer · Cross-border"
            name="Bharat Retail"
            role="National retail buyer"
            location="Bengaluru · sourcing for 200 stores"
            beforeLine="Sourcing handloom for a private label needed agents in Pochampally + 3 trips. 14-week lead from RFQ to first shipment."
            afterLine="One RFQ posted in English → 5 MSMEs read it in their language and quoted in 48 hours. Pilot of 200 sarees shipped in 21 days."
            quote="The fact that I can read a Pochampally weaver's pitch in real time is the unlock."
            kpis={[
              { v: "−85%", l: "Sourcing time" },
              { v: "1 RFQ", l: "Replaces 3 trips" },
              { v: "EN→TE→EN", l: "Auto translate" },
            ]}
          />

          <Story
            tag="Government · Public good"
            name="State Skill Mission (illustrative)"
            role="ITI graduates, 12K/yr cohort"
            location="Telangana"
            beforeLine="Spent ₹14K per learner on certification. 38% placement rate at 12 months. No visibility into who got hired or at what wage."
            afterLine="Issue verifiable badges to graduate profiles → live placement dashboard → wage uplift tracked per badge type. Outcome-linked funding becomes possible."
            quote="For the first time, our skilling rupee creates a measurable, public ledger of placements."
            kpis={[
              { v: "+41%", l: "Placement rate" },
              { v: "Live", l: "Wage tracking" },
              { v: "↑↑", l: "ROI on skilling" },
            ]}
          />
        </div>
      </Section>

      {/* BEFORE / AFTER DATA SLAB */}
      <section className="bg-[var(--as-bg-alt)] py-16 border-b border-[var(--as-border)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)] mb-3">By the numbers</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--as-primary)] mb-10" style={{fontFamily:"Inter"}}>
            What flips when skills become searchable.
          </h2>
          <div className="as-card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--as-primary)] text-[var(--as-bg)]">
                <tr>
                  <th className="text-left p-4 font-bold uppercase tracking-wider text-xs">Dimension</th>
                  <th className="text-left p-4 font-bold uppercase tracking-wider text-xs">India today (status quo)</th>
                  <th className="text-left p-4 font-bold uppercase tracking-wider text-xs">With Associate Skill</th>
                  <th className="text-left p-4 font-bold uppercase tracking-wider text-xs">Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-[var(--as-border)]">
                  <td className="p-4 font-bold text-[var(--as-primary)]">Time to hire a verified tradesperson</td>
                  <td className="p-4 text-[var(--as-text-secondary)]">14–28 days via contractors</td>
                  <td className="p-4 font-semibold">Under 24 hours, match-scored</td>
                  <td className="p-4"><span className="as-badge-pill">−92% time</span></td>
                </tr>
                <tr className="border-t border-[var(--as-border)]">
                  <td className="p-4 font-bold text-[var(--as-primary)]">Worker take-home as % of buyer spend</td>
                  <td className="p-4 text-[var(--as-text-secondary)]">~50–60% (rest is middleman + agent)</td>
                  <td className="p-4 font-semibold">85%+ (direct relationship)</td>
                  <td className="p-4"><span className="as-badge-pill">+30 pts</span></td>
                </tr>
                <tr className="border-t border-[var(--as-border)]">
                  <td className="p-4 font-bold text-[var(--as-primary)]">MSME discoverable radius</td>
                  <td className="p-4 text-[var(--as-text-secondary)]">~5 km (word-of-mouth)</td>
                  <td className="p-4 font-semibold">Global (multilingual + RFQ)</td>
                  <td className="p-4"><span className="as-badge-pill">∞×</span></td>
                </tr>
                <tr className="border-t border-[var(--as-border)]">
                  <td className="p-4 font-bold text-[var(--as-primary)]">Trust signal for households</td>
                  <td className="p-4 text-[var(--as-text-secondary)]">Phone-a-friend, social risk</td>
                  <td className="p-4 font-semibold">KYC + badges + reviews + video</td>
                  <td className="p-4"><span className="as-badge-pill">4 layers</span></td>
                </tr>
                <tr className="border-t border-[var(--as-border)]">
                  <td className="p-4 font-bold text-[var(--as-primary)]">Language addressable per worker</td>
                  <td className="p-4 text-[var(--as-text-secondary)]">1 (whatever they speak)</td>
                  <td className="p-4 font-semibold">EN + HI + TE today, 22 by Y2</td>
                  <td className="p-4"><span className="as-badge-pill">22×</span></td>
                </tr>
                <tr className="border-t border-[var(--as-border)]">
                  <td className="p-4 font-bold text-[var(--as-primary)]">Skilling spend → placement visibility</td>
                  <td className="p-4 text-[var(--as-text-secondary)]">Black box (~38% reported placement)</td>
                  <td className="p-4 font-semibold">Live ledger of placements + wages</td>
                  <td className="p-4"><span className="as-badge-pill">Outcome-linked</span></td>
                </tr>
                <tr className="border-t border-[var(--as-border)] bg-[var(--as-bg-alt)]">
                  <td className="p-4 font-bold text-[var(--as-accent)]">Net economic value unlocked</td>
                  <td className="p-4 text-[var(--as-text-secondary)]">Trapped in informal market</td>
                  <td className="p-4 font-bold text-[var(--as-primary)]">₹2–4 L per worker per year</td>
                  <td className="p-4"><span className="as-badge-pill">×10M = ₹2–4 L Cr</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-5">
            <div className="as-card p-6 border-l-4 !border-l-[var(--as-accent)]">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-accent)]">For workers</div>
              <p className="mt-3 text-[var(--as-text-secondary)] leading-relaxed">
                A 30% pay uplift for a ₹400/day worker = <strong className="text-[var(--as-primary)]">₹36,000 extra income/year</strong>.
                For 5M workers, that's <strong className="text-[var(--as-primary)]">₹18,000 Cr</strong> moved from
                middlemen to households — annually.
              </p>
            </div>
            <div className="as-card p-6 border-l-4 !border-l-[var(--as-accent)]">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-accent)]">For MSMEs</div>
              <p className="mt-3 text-[var(--as-text-secondary)] leading-relaxed">
                Even a 15% capacity utilisation lift across 250K MSMEs unlocks
                <strong className="text-[var(--as-primary)]"> ₹47,000 Cr</strong> in additional output —
                with zero new factories built.
              </p>
            </div>
            <div className="as-card p-6 border-l-4 !border-l-[var(--as-accent)]">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-accent)]">For buyers</div>
              <p className="mt-3 text-[var(--as-text-secondary)] leading-relaxed">
                Cutting hiring cycle by 90% on 50K employer-seats &amp; 1M annual hires
                saves <strong className="text-[var(--as-primary)]">~₹2,400 Cr</strong> in finder-fees and lost-productivity-while-vacant.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MARKET */}
      <Section id="market" kicker="Market size" title="A $200B+ opportunity hiding in plain sight.">
        <div className="grid md:grid-cols-3 gap-5 mb-10">
          <div className="as-card p-7">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">TAM</div>
            <div className="text-4xl font-extrabold text-[var(--as-primary)] mt-2" style={{fontFamily:"Inter"}}>$210B</div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-3 leading-relaxed">
              Global blue-collar + MSME staffing & sourcing spend. India alone = ₹4.5L Cr informal wage flow,
              touching 470M workers and 63M MSMEs.
            </p>
          </div>
          <div className="as-card p-7 border-2 !border-[var(--as-accent)]">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-accent)]">SAM</div>
            <div className="text-4xl font-extrabold text-[var(--as-primary)] mt-2" style={{fontFamily:"Inter"}}>$28B</div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-3 leading-relaxed">
              India + Bharat-adjacent geographies (Bangladesh, Gulf, SE Asia diaspora). Construction,
              beauty, repair, tailoring, fabrication — the high-frequency trades.
            </p>
          </div>
          <div className="as-card p-7">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">SOM (5 yr)</div>
            <div className="text-4xl font-extrabold text-[var(--as-primary)] mt-2" style={{fontFamily:"Inter"}}>$1.4B</div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-3 leading-relaxed">
              5M verified workers, 250K MSMEs, 50K active employers across Tier-1 to Tier-3 India + 3 export corridors.
            </p>
          </div>
        </div>

        <div className="as-card p-7">
          <div className="font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>Why now</div>
          <ul className="space-y-3 text-[var(--as-text-secondary)]">
            <li className="flex gap-3"><CheckCircle size={20} weight="fill" color="#22C55E" className="shrink-0 mt-0.5" />
              <span><strong className="text-[var(--as-primary)]">Smartphone + cheap data</strong> — 750M+ Indians now upload video. Two years ago, they couldn't.</span></li>
            <li className="flex gap-3"><CheckCircle size={20} weight="fill" color="#22C55E" className="shrink-0 mt-0.5" />
              <span><strong className="text-[var(--as-primary)]">AI translation collapsed to free</strong> — what cost ₹500/min in 2022 is now ₹0.05. Multilingual UX is finally affordable at scale.</span></li>
            <li className="flex gap-3"><CheckCircle size={20} weight="fill" color="#22C55E" className="shrink-0 mt-0.5" />
              <span><strong className="text-[var(--as-primary)]">Skill India Digital Hub + DigiLocker APIs</strong> — government rails make verified credentials portable for the first time.</span></li>
            <li className="flex gap-3"><CheckCircle size={20} weight="fill" color="#22C55E" className="shrink-0 mt-0.5" />
              <span><strong className="text-[var(--as-primary)]">ONDC + Bharat-stack momentum</strong> — buyers are now actively sourcing MSMEs via open networks, creating a pull for this layer.</span></li>
            <li className="flex gap-3"><CheckCircle size={20} weight="fill" color="#22C55E" className="shrink-0 mt-0.5" />
              <span><strong className="text-[var(--as-primary)]">Demographic dividend window</strong> — 65% of Indians are under 35; they need to enter the formal economy in the next 7 years or it shuts.</span></li>
          </ul>
        </div>
      </Section>

      {/* BENEFITS */}
      <Section kicker="Benefits" title="Every side of the marketplace wins.">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="as-card p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--as-bg-alt)] grid place-items-center border border-[var(--as-border)]">
                <Users size={26} weight="duotone" color="#080F14" />
              </div>
              <div>
                <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Skilled workers</div>
                <div className="text-xs text-[var(--as-text-secondary)]">Electricians, welders, beauticians, carpenters, drivers…</div>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[var(--as-text-secondary)]">
              <li>• Earn <strong>30-50% more</strong> by removing middlemen and pricing on proof, not promise</li>
              <li>• Reach buyers in any language — Telugu skill, English contract</li>
              <li>• Build a portable credential trail (badges + reviews + media)</li>
              <li>• Onboard in 5 minutes with a phone, no résumé, no English required</li>
            </ul>
          </div>

          <div className="as-card p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--as-bg-alt)] grid place-items-center border border-[var(--as-border)]">
                <Buildings size={26} weight="duotone" color="#080F14" />
              </div>
              <div>
                <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>MSMEs</div>
                <div className="text-xs text-[var(--as-text-secondary)]">Workshops, co-ops, service providers, manufacturers</div>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[var(--as-text-secondary)]">
              <li>• Get discoverable beyond their <strong>5km word-of-mouth radius</strong></li>
              <li>• Showcase capacity + sectors + export-readiness with one profile</li>
              <li>• Unlock <strong>RFQs from national + global buyers</strong> via search</li>
              <li>• Verify their team's skills under one umbrella brand</li>
            </ul>
          </div>

          <div className="as-card p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--as-bg-alt)] grid place-items-center border border-[var(--as-border)]">
                <Briefcase size={26} weight="duotone" color="#080F14" />
              </div>
              <div>
                <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Employers & buyers</div>
                <div className="text-xs text-[var(--as-text-secondary)]">Builders, retailers, households, global buyers</div>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[var(--as-text-secondary)]">
              <li>• Cut hiring time from <strong>weeks to hours</strong> with skill-overlap match scoring</li>
              <li>• See video proof before paying a single rupee — hire confidently</li>
              <li>• Tap pre-verified pools — KYC, ID, training-partner badges built-in</li>
              <li>• Source globally without language friction (auto-translated transcripts)</li>
            </ul>
          </div>

          <div className="as-card p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--as-bg-alt)] grid place-items-center border border-[var(--as-border)]">
                <GraduationCap size={26} weight="duotone" color="#080F14" />
              </div>
              <div>
                <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Training partners & government</div>
                <div className="text-xs text-[var(--as-text-secondary)]">ITIs, NSDC, Skill India, state missions</div>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[var(--as-text-secondary)]">
              <li>• Convert skilling spend into <strong>measurable placements</strong></li>
              <li>• Issue and revoke verifiable badges that travel with the worker</li>
              <li>• Use real demand signals to plan future curricula</li>
              <li>• Build a transparent ledger of who skilled whom — public-good infrastructure</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* BUSINESS MODEL */}
      <Section id="model" kicker="Business model" title="Three revenue streams · all flywheel-aligned.">
        <div className="as-card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--as-bg-alt)] text-[var(--as-text-secondary)]">
              <tr>
                <th className="text-left p-4">Stream</th>
                <th className="text-left p-4">Who pays</th>
                <th className="text-left p-4">How</th>
                <th className="text-left p-4">Phase</th>
              </tr>
            </thead>
            <tbody>
              <RevenueRow stream="Employer subscriptions" who="Companies, builders, retail buyers"
                model="₹2,000–25,000/month for unlimited search, RFQ posting, applicant pipelines" tag="Year 1" />
              <RevenueRow stream="Verification fees" who="Workers + MSMEs"
                model="₹99–499 per badge: ID, certificate, on-site skill test (revenue share with training partners)" tag="Year 1" />
              <RevenueRow stream="Take-rate on gigs/RFQs" who="Buyer side"
                model="3–8% transaction fee on contracts closed via the platform (escrow optional)" tag="Year 2" />
              <RevenueRow stream="Government skilling contracts" who="State missions, NSDC, MNREGA-link"
                model="Per-placement bounty + SaaS license for state skill missions" tag="Year 2" />
              <RevenueRow stream="Cross-border sourcing" who="Global buyers (Gulf, US, EU)"
                model="Concierge sourcing fee + per-shipment compliance + payment rail margin" tag="Year 3" />
              <RevenueRow stream="Worker financial services" who="Workers (B2B2C)"
                model="Embedded credit, insurance, savings — partner banks share interchange + premium" tag="Year 3" />
            </tbody>
          </table>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          <div className="as-card p-6">
            <Wallet size={28} weight="duotone" color="#00B7A6" />
            <div className="mt-3 font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Unit economics</div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-1.5 leading-relaxed">
              CAC ~₹150 (referral + WhatsApp loops). ARPU on employer side ~₹8,000/mo. LTV/CAC &gt; 12 within 18 months.
            </p>
          </div>
          <div className="as-card p-6">
            <ChartLineUp size={28} weight="duotone" color="#00B7A6" />
            <div className="mt-3 font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Network effects</div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-1.5 leading-relaxed">
              Each verified worker raises the trust ceiling for the whole platform.
              Each employer match creates 5+ peer referrals on the worker side.
            </p>
          </div>
          <div className="as-card p-6">
            <Handshake size={28} weight="duotone" color="#00B7A6" />
            <div className="mt-3 font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Defensibility</div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-1.5 leading-relaxed">
              The reputation graph (badges + reviews + media + match history) is non-portable.
              Data compound + multilingual training set get better every week.
            </p>
          </div>
        </div>
      </Section>

      {/* COMPETITION */}
      <Section kicker="Competitive landscape" title="There is no skills-first, video-native, multilingual platform — yet.">
        <div className="overflow-x-auto as-card p-0">
          <table className="w-full text-sm">
            <thead className="bg-[var(--as-bg-alt)] text-[var(--as-text-secondary)]">
              <tr>
                <th className="text-left p-4">Player</th>
                <th className="text-left p-4">Approach</th>
                <th className="text-left p-4">Gap we fill</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-[var(--as-border)]">
                <td className="p-4 font-bold">LinkedIn / Naukri</td>
                <td className="p-4 text-[var(--as-text-secondary)]">Résumé-first, white-collar, English-only</td>
                <td className="p-4">Excludes 92% of workforce; no video; no MSME layer</td>
              </tr>
              <tr className="border-t border-[var(--as-border)]">
                <td className="p-4 font-bold">Apna / Workindia</td>
                <td className="p-4 text-[var(--as-text-secondary)]">Job-board for blue-collar, text-first</td>
                <td className="p-4">No verified skill graph; no portfolio; no MSME side</td>
              </tr>
              <tr className="border-t border-[var(--as-border)]">
                <td className="p-4 font-bold">UrbanCompany</td>
                <td className="p-4 text-[var(--as-text-secondary)]">Closed marketplace for services</td>
                <td className="p-4">Vertical-locked; not multilingual; not multi-stakeholder</td>
              </tr>
              <tr className="border-t border-[var(--as-border)]">
                <td className="p-4 font-bold">IndiaMART / TradeIndia</td>
                <td className="p-4 text-[var(--as-text-secondary)]">B2B catalog, lead-gen</td>
                <td className="p-4">Catalogue, not credentials; no individual workers</td>
              </tr>
              <tr className="border-t border-[var(--as-border)]">
                <td className="p-4 font-bold">Skill India Digital Hub</td>
                <td className="p-4 text-[var(--as-text-secondary)]">Govt skilling registry</td>
                <td className="p-4">No marketplace layer or hiring engine — we plug in</td>
              </tr>
              <tr className="border-t border-[var(--as-border)] bg-[var(--as-bg-alt)]">
                <td className="p-4 font-bold text-[var(--as-accent)]">Associate Skill</td>
                <td className="p-4">Skills-first · video-native · multilingual · multi-role</td>
                <td className="p-4">Worker + MSME + Employer + Govt in one trust graph</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* TRACTION (DEMO) */}
      <Section kicker="Traction · live MVP" title="Already moving in the demo data you're using right now.">
        <p className="text-[var(--as-text-secondary)] max-w-3xl mb-8 leading-relaxed">
          What you see on this site is a working MVP — not a prototype. Real auth, real DB, real moderation,
          real matching engine. The numbers below come from the seeded demo, but every endpoint is production-shaped.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat value="11" label="Live profiles" sub="Across 4 roles, 3 languages, 5 cities" tid="trac-stat-1" />
          <Stat value="12" label="Skill videos & images" sub="Auto-screened by our trust pipeline before publish" tid="trac-stat-2" />
          <Stat value="5" label="Open opportunities" sub="Job, gig, RFQ, apprenticeship — all live" tid="trac-stat-3" />
          <Stat value="6" label="Applications in flight" sub="With shortlist + selection + match-score in DB" tid="trac-stat-4" />
        </div>

        <div className="as-card p-7 mt-8 flex items-start gap-4">
          <Quotes size={32} weight="duotone" color="#00B7A6" className="shrink-0" />
          <p className="text-lg text-[var(--as-primary)] leading-relaxed" style={{fontFamily:"Inter"}}>
            "Outstanding work on our wardrobe. Finished early and clean."
            <br />
            <span className="text-sm text-[var(--as-text-secondary)] font-normal">
              — Meera, Nimbus Homes · ★ 5.0 review on Ravi Kumar (Master Carpenter)
            </span>
          </p>
        </div>
      </Section>

      {/* SPONSORS / CSR */}
      <Section kicker="Sponsors & CSR · already live" title="A third demand-side: India's ₹25,000 Cr/yr CSR pool, audit-ready.">
        <p className="text-lg text-[var(--as-text-secondary)] max-w-4xl leading-relaxed mb-10">
          We don't just sell to employers and governments. We've built a sponsor rail so corporates,
          foundations, brands, banks, and even individual NRI patrons can directly fund worker
          verifications and MSME upgrades — with audit-grade impact reporting baked in.
        </p>

        <div className="grid md:grid-cols-3 gap-5">
          <Pillar icon={Buildings} title="Corporate CSR (Schedule VII)"
            desc="₹25,000 Cr/yr is mandated to flow into education, skill-development, gender equality, rural development. We map every program to Schedule VII and produce board-ready PDFs." />
          <Pillar icon={Handshake} title="Brand sponsorship"
            desc="Bosch, Asian Paints, Pidilite — brands fund cohorts, get co-branded badges on worker profiles. Workers love the trust uplift; brands get demonstrated affinity with the trades." />
          <Pillar icon={HandHeart} title="Diaspora & individual patrons"
            desc="An NRI alumnus sponsors 10 carpenters from her hometown for ₹50K. A ₹500 'Power a Worker' donation funds 1 month of platform reach for a tailor. Micro-philanthropy meets viral mechanics." />
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-8">
          <div className="as-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-accent)]">Live in the demo</div>
            <div className="font-bold text-[var(--as-primary)] mt-2 text-lg" style={{fontFamily:"Inter"}}>
              Bosch Electrician 1000 — Karnataka
            </div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-2 leading-relaxed">
              ₹50 lakh budget · 1,000 slots · 247 already approved · co-branded "Bosch Verified Electrician"
              badge issued to every funded worker. Schedule VII (ii) Skill development.
            </p>
          </div>
          <div className="as-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-accent)]">Live in the demo</div>
            <div className="font-bold text-[var(--as-primary)] mt-2 text-lg" style={{fontFamily:"Inter"}}>
              Tata Weaver Verification — Pochampally
            </div>
            <p className="text-sm text-[var(--as-text-secondary)] mt-2 leading-relaxed">
              ₹25 lakh budget · 500 women weavers · 73 already verified · multilingual RFQ-ready profiles.
              Schedule VII (x) Rural development.
            </p>
          </div>
        </div>

        <div className="as-card p-7 mt-8 bg-[var(--as-bg-alt)]">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-3xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>10–15%</div>
              <div className="text-xs uppercase tracking-wider font-bold text-[var(--as-text-secondary)] mt-1">Take-rate on sponsor pools</div>
              <p className="text-sm text-[var(--as-text-secondary)] mt-2">
                A single ₹50 L Bosch program = ₹5–7 L direct margin to the platform.
              </p>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>~₹0</div>
              <div className="text-xs uppercase tracking-wider font-bold text-[var(--as-text-secondary)] mt-1">Worker-side CAC</div>
              <p className="text-sm text-[var(--as-text-secondary)] mt-2">
                Sponsors fund the verification, workers come for free, badges become the viral hook.
              </p>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>3</div>
              <div className="text-xs uppercase tracking-wider font-bold text-[var(--as-text-secondary)] mt-1">Demand-side legs</div>
              <p className="text-sm text-[var(--as-text-secondary)] mt-2">
                Employers (transactional) + governments (volume) + sponsors (strategic) = 3-legged revenue stool.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* THE ASK */}
      <Section kicker="The ask" title="Seed: ₹X Cr to ship 5M trust signals in 18 months.">
        <div className="grid md:grid-cols-3 gap-5">
          <div className="as-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">Use of funds</div>
            <ul className="mt-3 space-y-2 text-sm text-[var(--as-text-secondary)]">
              <li>• <strong className="text-[var(--as-primary)]">40%</strong> — Field activation in 5 cities (worker + MSME onboarding teams)</li>
              <li>• <strong className="text-[var(--as-primary)]">25%</strong> — Engineering (real STT, video moderation, govt API integrations)</li>
              <li>• <strong className="text-[var(--as-primary)]">20%</strong> — Training-partner BD + government pilots</li>
              <li>• <strong className="text-[var(--as-primary)]">15%</strong> — Brand + multilingual content engine</li>
            </ul>
          </div>
          <div className="as-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">18-month milestones</div>
            <ul className="mt-3 space-y-2 text-sm text-[var(--as-text-secondary)]">
              <li>• 500K verified workers · 25K MSMEs · 5K paying employers</li>
              <li>• 3 state skill-mission pilots live</li>
              <li>• Multi-modal content moderation (image + video + audio)</li>
              <li>• Cross-border sourcing pilot (Gulf corridor)</li>
              <li>• ARR ₹40 Cr · gross margin 70%+</li>
            </ul>
          </div>
          <div className="as-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">Why we'll win</div>
            <ul className="mt-3 space-y-2 text-sm text-[var(--as-text-secondary)]">
              <li>• Multilingual + video-native is a 3-year head start the moment we ship</li>
              <li>• Trust graph compounds — first 100K verified workers create a moat</li>
              <li>• Govt rail integration locks in distribution at near-zero CAC</li>
              <li>• Founders + advisors = trade craftsmanship × consumer marketplaces × AI infra</li>
            </ul>
          </div>
        </div>

        <div className="as-card p-8 mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <Lightbulb size={36} weight="duotone" color="#00B7A6" />
            <h3 className="text-2xl font-bold mt-3 text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>
              We're building the LinkedIn for the 92%.
            </h3>
            <p className="text-[var(--as-text-secondary)] mt-2 max-w-2xl">
              If you back marketplaces, AI infra, financial inclusion, India 2.0, or the global shift to skill-based hiring —
              this sits at the intersection of all five.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/" className="btn-primary" data-testid="investors-cta-product">
              Explore the live product <ArrowRight size={18} weight="bold" />
            </Link>
            <a href="https://mythosindiastudio.com/contact" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="investors-cta-email">
              <Handshake size={18} weight="duotone" /> Talk to founders ↗
            </a>
          </div>
        </div>

        {/* IF WE WIN */}
        <div className="mt-12 grid md:grid-cols-2 gap-5">
          <div className="as-card p-7 bg-[var(--as-primary)] text-[var(--as-bg)] !border-[var(--as-primary)]">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#F5B27D] mb-3">If we win</div>
            <h3 className="text-2xl font-bold mb-4" style={{fontFamily:"Inter"}}>
              The default way 500M+ people get hired changes.
            </h3>
            <ul className="space-y-2.5 text-[var(--as-bg)]/90 text-sm leading-relaxed">
              <li>• ₹2–4 lakh of annual income moves into each worker's pocket — not a contractor's</li>
              <li>• MSMEs export-ready in 90 days, not 90 months</li>
              <li>• Govt skilling spend becomes outcome-linked for the first time</li>
              <li>• A new global standard: hire by demonstrated skill, not credentialed pedigree</li>
              <li>• A reputation graph that compounds — every review, every badge, every match</li>
            </ul>
          </div>

          <div className="as-card p-7">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)] mb-3">If we don't</div>
            <h3 className="text-2xl font-bold mb-4 text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>
              The opportunity cost is staggering.
            </h3>
            <ul className="space-y-2.5 text-[var(--as-text-secondary)] text-sm leading-relaxed">
              <li>• 10M skilled grads/year keep losing 70% of earning potential to invisibility</li>
              <li>• Middlemen continue to capture ₹4.2L Cr/year that should reach households</li>
              <li>• MSMEs stay stuck within a 5km discoverable radius forever</li>
              <li>• India's demographic dividend window (7 years) closes without a formal trust layer</li>
              <li>• A Western platform eventually does it — and exports our craftsmen back to us</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 as-card p-10 text-center">
          <Quotes size={32} weight="duotone" color="#00B7A6" className="mx-auto" />
          <p className="text-xl sm:text-2xl md:text-3xl mt-4 leading-[1.4] font-bold text-[var(--as-primary)] max-w-4xl mx-auto" style={{fontFamily:"Inter"}}>
            Every great economy has a layer that turns invisible work into visible trust.<br />
            <span className="text-[var(--as-accent)]">India's is unbuilt.</span>
            <br />We're building it.
          </p>
        </div>
      </Section>

      <footer className="py-10 border-t border-[var(--as-border)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-[var(--as-text-secondary)]">
            <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Associate Skill · Investor brief</div>
            <div className="text-xs mt-1 max-w-2xl leading-relaxed">
              Sources: NSDC Skill Gap Reports · Periodic Labour Force Survey (PLFS) ·
              Ministry of MSME · World Economic Forum Future of Jobs · World Bank India.
            </div>
          </div>
          <a href="https://mythosindiastudio.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/mythiva-logo.png" alt="MYTHIVA™" className="h-8 w-auto object-contain bg-white p-1 rounded-lg border border-[var(--as-border)]" />
            <div className="text-xs leading-tight">
              <div className="uppercase tracking-widest font-bold text-[var(--as-text-secondary)]">A product of</div>
              <div className="font-extrabold text-[var(--as-primary)]">Mythos India Studios Pvt Ltd</div>
              <div className="text-[10px] text-[var(--as-text-secondary)]">CIN · U62010TS2026PTC221089</div>
            </div>
          </a>
        </div>
      </footer>
    </Layout>
  );
}
